/**
 * Professional Email Service for vitarpm System
 * Production-ready email service with comprehensive error handling
 * Supports millions of users with queue system and monitoring
 */

import api from './api';

class EmailService {
  constructor() {
    this.apiClient = api;

    // Log only real email routes — this service shares the global `api` instance, so without
    // filtering, every calendar/auth/etc. request would be mislabeled as an "Email API" call.
    this.apiClient.interceptors.request.use(
      (config) => {
        const u = config.url || '';
        const isEmailRequest =
          u.includes('/email') || u.includes('email-');
        if (isEmailRequest && process.env.NODE_ENV === 'development') {
          console.log(`📧 Email API Request: ${config.method?.toUpperCase()} ${u}`);
        }
        return config;
      },
      (error) => {
        console.error('📧 Email API Request Error:', error);
        return Promise.reject(error);
      }
    );
  }

  /**
   * Send practice creation notification emails
   * @param {Object} practiceData - Practice information
   * @param {Object} adminData - Admin information
   * @param {Object} notificationSettings - Email notification preferences
   * @returns {Promise<Object>} - Email sending results
   */
  async sendPracticeCreationNotifications(practiceData, adminData, notificationSettings) {
    try {
      const emailData = {
        practice: practiceData,
        admin: adminData,
        notifications: notificationSettings,
        timestamp: new Date().toISOString(),
      };

      const response = await this.apiClient.post('/email/practice-creation', emailData);

      return {
        success: true,
        data: response.data,
        message: 'Practice creation notifications sent successfully'
      };
    } catch (error) {
      console.error('❌ Practice creation email error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        message: 'Failed to send practice creation notifications'
      };
    }
  }

  /**
   * Send patient addition notification emails
   * @param {Object} patientData - Patient information
   * @param {Object} practiceData - Practice information
   * @param {Object} notificationSettings - Email notification preferences
   * @returns {Promise<Object>} - Email sending results
   */
  async sendPatientAdditionNotifications(patientData, practiceData, notificationSettings) {
    try {
      const emailData = {
        patient: patientData,
        practice: practiceData,
        notifications: notificationSettings,
        timestamp: new Date().toISOString(),
      };

      const response = await this.apiClient.post('/email/patient-addition', emailData);

      return {
        success: true,
        data: response.data,
        message: 'Patient addition notifications sent successfully'
      };
    } catch (error) {
      console.error('❌ Patient addition email error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        message: 'Failed to send patient addition notifications'
      };
    }
  }

  /**
   * Send custom notification email
   * @param {Object} emailConfig - Email configuration
   * @returns {Promise<Object>} - Email sending results
   */
  async sendCustomNotification(emailConfig) {
    try {
      const response = await this.apiClient.post('/email/custom', emailConfig);

      return {
        success: true,
        data: response.data,
        message: 'Custom notification sent successfully'
      };
    } catch (error) {
      console.error('❌ Custom email error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        message: 'Failed to send custom notification'
      };
    }
  }

  /**
   * Get email delivery status
   * @param {string} emailId - Email ID to check
   * @returns {Promise<Object>} - Email status
   */
  async getEmailStatus(emailId) {
    try {
      const response = await this.apiClient.get(`/email/status/${emailId}`);

      return {
        success: true,
        data: response.data,
        message: 'Email status retrieved successfully'
      };
    } catch (error) {
      console.error('❌ Email status error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        message: 'Failed to get email status'
      };
    }
  }

  /**
   * Get email logs for monitoring
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} - Email logs
   */
  async getEmailLogs(filters = {}) {
    try {
      const response = await this.apiClient.get('/email/logs', { params: filters });

      return {
        success: true,
        data: response.data,
        message: 'Email logs retrieved successfully'
      };
    } catch (error) {
      console.error('❌ Email logs error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        message: 'Failed to get email logs'
      };
    }
  }
}

// Create singleton instance
const emailService = new EmailService();

export default emailService;
